import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor, convertMoneyToNumber } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getCountryDialect,
  getAvailableTeachingLanguages,
  getTeachingCategoriesFromPublicData,
  getTutorPriceBand,
} from '../../util/constants';
import { convertCurrency } from '../../util/currencyConversion';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_REVIEW_SUMMARIES_REQUEST = 'app/SearchPage/FETCH_REVIEW_SUMMARIES_REQUEST';
export const FETCH_REVIEW_SUMMARIES_SUCCESS = 'app/SearchPage/FETCH_REVIEW_SUMMARIES_SUCCESS';
export const FETCH_REVIEW_SUMMARIES_ERROR = 'app/SearchPage/FETCH_REVIEW_SUMMARIES_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  reviewSummaries: {},
  fallbackNotice: null,
};

const resultIds = data => {
  const listings = data.data || [];
  const included = data.included || [];

  const authorMap = {};
  included.forEach(item => {
    if (item.type === 'user') {
      authorMap[item.id.uuid] = item.attributes?.profile?.publicData || {};
    }
  });

  return listings
    .filter(l => {
      const authorId = l.relationships?.author?.data?.id?.uuid;
      const authorPublicData = authorId ? authorMap[authorId] : null;
      const isAuthorUnderReview = authorPublicData?.underReview === true;

      return (
        !l.attributes.deleted &&
        l.attributes.state === 'published' &&
        l?.attributes?.publicData?.listingType !== 'blogs' &&
        !isAuthorUnderReview
      );
    })
    .map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
        fallbackNotice: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
        fallbackNotice: payload.fallbackNotice || null,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    case FETCH_REVIEW_SUMMARIES_REQUEST:
      return state;
    case FETCH_REVIEW_SUMMARIES_SUCCESS:
      return {
        ...state,
        reviewSummaries: { ...state.reviewSummaries, ...payload },
      };
    case FETCH_REVIEW_SUMMARIES_ERROR:
      return state;
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = (response, fallbackNotice = null) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, fallbackNotice },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewSummariesRequest = () => ({ type: FETCH_REVIEW_SUMMARIES_REQUEST });
export const fetchReviewSummariesSuccess = summariesMap => ({
  type: FETCH_REVIEW_SUMMARIES_SUCCESS,
  payload: summariesMap,
});
export const fetchReviewSummariesError = e => ({
  type: FETCH_REVIEW_SUMMARIES_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewSummaries = listingIds => (dispatch, getState, sdk) => {
  if (!Array.isArray(listingIds) || listingIds.length === 0) return Promise.resolve();

  const toUuid = id => (typeof id === 'string' ? id : id?.uuid || id?.toString());
  const uniqueIds = Array.from(new Set(listingIds.map(toUuid).filter(Boolean)));

  // Get already cached review summaries from state
  const state = getState();
  const cachedSummaries = state.SearchPage?.reviewSummaries || {};

  // Filter out IDs that already have cached summaries
  const idsToFetch = uniqueIds.filter(uuid => !cachedSummaries[uuid]);

  // If all summaries are already cached, return immediately
  if (idsToFetch.length === 0) {
    return Promise.resolve();
  }

  dispatch(fetchReviewSummariesRequest());

  // Batch fetch reviews for multiple listings at once instead of N+1 queries
  // This significantly reduces network requests and improves performance
  const BATCH_SIZE = 10; // Fetch reviews for up to 10 listings per request
  const batches = [];

  for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
    const batchIds = idsToFetch.slice(i, i + BATCH_SIZE);
    batches.push(
      sdk.reviews
        .query({
          listing_id: batchIds.join(','), // Batch multiple IDs in one query
          state: 'public',
          limit: 100, // Limit results per batch
        })
        .then(resp => {
          const reviews = denormalisedResponseEntities(resp);
          // Group reviews by listing_id
          const reviewsByListing = {};
          batchIds.forEach(uuid => {
            reviewsByListing[uuid] = [];
          });

          reviews.forEach(review => {
            const listingId = review?.attributes?.listingId?.uuid;
            if (listingId && reviewsByListing[listingId]) {
              reviewsByListing[listingId].push(review);
            }
          });

          // Calculate summaries for each listing
          const summaries = {};
          Object.entries(reviewsByListing).forEach(([uuid, listingReviews]) => {
            const count = listingReviews.length;
            const avg = count
              ? listingReviews.reduce((s, r) => s + (r?.attributes?.rating || 0), 0) / count
              : 0;
            summaries[uuid] = { avg, count };
          });

          return summaries;
        })
        .catch(e => {
          // Log error but don't fail the entire batch
          console.error('Error fetching reviews batch:', e);
          return {};
        })
    );
  }

  return Promise.all(batches)
    .then(results => {
      // Merge all batch results
      const map = results.reduce((acc, batchMap) => {
        return { ...acc, ...batchMap };
      }, {});
      dispatch(fetchReviewSummariesSuccess(map));
    })
    .catch(e => dispatch(fetchReviewSummariesError(storableError(e))));
};

// In-memory cache for search listings responses to eliminate redundant sdk.listings.query calls
// when users navigate back and forth between search results and tutor profile pages.
const searchListingsCache = new Map();
const SEARCH_LISTINGS_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

const getSearchCacheKey = queryParams => {
  try {
    const sorted = {};
    Object.keys(queryParams || {})
      .sort()
      .forEach(k => {
        if (queryParams[k] !== undefined) {
          sorted[k] = queryParams[k];
        }
      });
    return JSON.stringify(sorted);
  } catch (err) {
    return null;
  }
};

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    const foundCategory = categories.find(cat => cat.id === levelValue);
    const subcategories = foundCategory?.subcategories || [];
    // Note: we might need to prepare nested categories too: categoryLevel1, categoryLevel2, categoryLevel3
    return foundCategory && subcategories.length > 0
      ? {
          [levelKey]: levelValue,
          ...constructCategoryPropertiesForAPI(queryParamPrefix, subcategories, level + 1, params),
        }
      : foundCategory
      ? { [levelKey]: levelValue }
      : {};
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => f.schemaType === 'long');
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // Prepare tutorMinRating (enum UI -> long range in API)
  // Accepts either a single value (e.g. '30') or a range 'min,max'. Converts to exclusive max: 'min,max+1'
  const prepareTutorMinRatingParam = (paramName, params) => {
    const ratingParamName = constructQueryParamName('tutorMinRating', 'public'); // 'pub_tutorMinRating'
    if (paramName !== ratingParamName) return {};
    const raw = params?.[ratingParamName];
    if (!raw) return {};
    const parts = String(raw).split(',');
    let min = parts[0];
    let max = parts[1];
    if (!max) {
      // Transform single value to range 'min,50'
      const n = Number(min);
      if (Number.isNaN(n)) return {};
      const clamped = Math.max(0, Math.min(50, Math.round(n)));
      min = String(clamped);
      max = '50';
    }
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1; // API expects exclusive upper bound for long
    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [ratingParamName]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  /**
   * Price filter parameter preparation for Sharetribe Flex API.
   * Converts user-selected price bounds (in active display currency, e.g. AED)
   * to base marketplace currency (USD) and formats into subunits.
   */
  const priceSearchParams = (priceParam, userCurrency = 'AED', rates = null) => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    if (!priceParam || values.length !== 2) {
      return {};
    }

    const minRaw = Number(values[0]);
    const maxRaw = Number(values[1]);
    if (isNaN(minRaw) || isNaN(maxRaw)) {
      return {};
    }

    // Convert values from user's active display currency (e.g. AED) to marketplace base currency (USD)
    // Subtract a small buffer on min to prevent edge cases with rounding / ceilings
    const minUSD = Math.max(0, convertCurrency(minRaw, userCurrency, 'USD', rates).amount - 1);
    const maxUSD = convertCurrency(maxRaw, userCurrency, 'USD', rates).amount + 1;

    return {
      price: [
        inSubunits(minUSD),
        inSubunits(maxUSD) + 1,
      ].join(','),
    };
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    targetCountry,
    dialectCountry,
    ...restOfParams
  } = searchParams;
  // Read current currency state to properly translate user currency inputs to USD
  const state = typeof getState === 'function' ? getState() : {};
  const selectedCurrency = state.currency?.selectedCurrency || 'AED';
  const fxRates = state.currency?.fxRates || null;

  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price, selectedCurrency, fxRates);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const params = {
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...prepareAPIParams(restOfParams, [
      prepareCategoryParams,
      prepareIntegerRangeParam,
      prepareTutorMinRatingParam,
    ]),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    perPage,
  };

  const queryListingsWithFallback = queryParams => {
    const cacheKey = getSearchCacheKey(queryParams);
    if (cacheKey && searchListingsCache.has(cacheKey)) {
      const entry = searchListingsCache.get(cacheKey);
      if (Date.now() - entry.timestamp < SEARCH_LISTINGS_CACHE_TTL_MS) {
        return Promise.resolve(entry.response);
      }
      searchListingsCache.delete(cacheKey);
    }

    const saveToCache = res => {
      if (cacheKey && res) {
        searchListingsCache.set(cacheKey, {
          timestamp: Date.now(),
          response: res,
        });
        if (searchListingsCache.size > 50) {
          const oldestKey = searchListingsCache.keys().next().value;
          searchListingsCache.delete(oldestKey);
        }
      }
      return res;
    };

    return sdk.listings
      .query(queryParams)
      .then(response => {
        // If pub_teachingLanguages, pub_teachFromZero, or pub_teaching_categories was queried but returned 0 results,
        // check if fallback query without them finds listings that have these attributes in unindexed profile data
        const hasUnindexedFilter = Boolean(
          queryParams.pub_teachingLanguages ||
          queryParams.pub_teachFromZero ||
          queryParams.pub_teaching_categories
        );
        if (
          hasUnindexedFilter &&
          (!response?.data?.data || response.data.data.length === 0)
        ) {
          const { pub_teachingLanguages, pub_teachFromZero, pub_teaching_categories, ...safeParams } = queryParams;
          return sdk.listings.query(safeParams).then(saveToCache);
        }
        return saveToCache(response);
      })
      .catch(err => {
        // If Sharetribe search schema doesn't yet have custom attributes indexed,
        // fallback to querying without them so search succeeds, then filter in-memory.
        if (
          queryParams.pub_teachingLanguages ||
          queryParams.pub_teachFromZero ||
          queryParams.pub_teaching_categories
        ) {
          const { pub_teachingLanguages, pub_teachFromZero, pub_teaching_categories, ...safeParams } = queryParams;
          return sdk.listings.query(safeParams).then(saveToCache);
        }
        throw err;
      });
  };

/**
 * Expands dialect search parameters to ensure bidirectional compatibility between
 * country codes (e.g. 'EG', 'LB', 'AE') and legacy dialect strings ('egyptian', 'levantine', 'gulf', etc.).
 */
const expandPubDialects = rawVal => {
  if (!rawVal) return rawVal;
  const prefixMatch = String(rawVal).match(/^(has_any|has_all):/);
  const prefix = prefixMatch ? prefixMatch[0] : '';
  const cleanStr = String(rawVal).replace(/^(has_any|has_all):/, '');
  const tokens = cleanStr.split(',').map(s => s.trim()).filter(Boolean);
  const expanded = new Set();
  tokens.forEach(token => {
    expanded.add(token);
    const cd = getCountryDialect(token);
    if (cd) {
      expanded.add(cd.countryCode);
      expanded.add(cd.dialectName.toLowerCase());
      // Specific legacy alias mappings for marketplace listings
      if (cd.countryCode === 'LB') expanded.add('levantine');
      if (cd.countryCode === 'AE') expanded.add('gulf');
      if (cd.countryCode === 'MA') expanded.add('moroccan');
      if (cd.countryCode === 'EG') expanded.add('egyptian');
      if (cd.countryCode === 'SY') expanded.add('syrian');
      if (cd.countryCode === 'PS') expanded.add('palestinian');
      if (cd.countryCode === 'IQ') expanded.add('iraqi');
    }
  });
  return `${prefix}${Array.from(expanded).join(',')}`;
};

  const expandedParams = params.pub_dialects
    ? { ...params, pub_dialects: expandPubDialects(params.pub_dialects) }
    : params;

  return queryListingsWithFallback(expandedParams)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // Country & dialect prioritization sort
      const selectedTargetCountry = targetCountry || dialectCountry;
      const countryDialect = selectedTargetCountry
        ? getCountryDialect(selectedTargetCountry)
        : null;
      const exactCountry = countryDialect
        ? countryDialect.countryCode
        : selectedTargetCountry
        ? String(selectedTargetCountry).toUpperCase()
        : null;
      const sisterCountries =
        countryDialect?.sisterCountries || (exactCountry ? [exactCountry] : []);
      const clusterName = countryDialect?.cluster?.toLowerCase();
      const exactDialectName = countryDialect?.dialectName?.toLowerCase();

      // Build author lookup map from included entities
      const authorMap = new Map();
      (response?.data?.included || []).forEach(item => {
        if (item.type === 'user') {
          authorMap.set(item.id?.uuid, item);
        }
      });

      const getListingCountry = l => {
        const pd = l?.attributes?.publicData || {};
        const authorId = l?.relationships?.author?.data?.id?.uuid;
        const author = authorMap.get(authorId);
        const authorPd = author?.attributes?.profile?.publicData || {};

        return (
          pd.countryOfOrigin ||
          pd.dialectCountry ||
          authorPd.countryOfOrigin ||
          authorPd.dialectCountry ||
          pd.country ||
          authorPd.country ||
          ''
        ).toUpperCase();
      };

      const getListingDialects = l => {
        const pd = l?.attributes?.publicData || {};
        const authorId = l?.relationships?.author?.data?.id?.uuid;
        const author = authorMap.get(authorId);
        const authorPd = author?.attributes?.profile?.publicData || {};

        const d1 = Array.isArray(pd.dialects) ? pd.dialects : [];
        const d2 = Array.isArray(authorPd.dialects) ? authorPd.dialects : [];
        return [...d1, ...d2].map(d => String(d).toLowerCase());
      };

      const getCountryPriority = l => {
        if (!exactCountry) return 0;
        const lCountry = getListingCountry(l);
        const lDialects = getListingDialects(l);

        // 1. Exact country / dialect match (e.g. Lebanese)
        if (lCountry === exactCountry) return 1;
        if (exactDialectName && lDialects.includes(exactDialectName)) return 1;

        // 2. Sister cluster match (e.g. Syrian, Jordanian, Palestinian for Lebanese)
        if (sisterCountries.includes(lCountry)) {
          const idx = sisterCountries.indexOf(lCountry);
          return 2 + (idx >= 0 ? idx * 0.05 : 0.5);
        }
        if (clusterName && lDialects.includes(clusterName)) {
          return 3;
        }

        // 3. Modern Standard Arabic (MSA) fallback match when no cluster tutors exist
        if (lCountry === 'MSA' || lDialects.includes('msa') || lDialects.includes('modern standard arabic')) {
          return 4;
        }

        // 4. Other tutors
        return 10;
      };

      // If rating filter is active, sort results so the closest to the selected threshold
      // (i.e. exact stars first) appear first, then higher stars.
      const ratingParamName = constructQueryParamName('tutorMinRating', 'public');
      const selectedMinStr = params?.[ratingParamName]
        ? String(params[ratingParamName]).split(',')[0]
        : null;
      const selectedMin = selectedMinStr ? parseInt(selectedMinStr, 10) : NaN;
      const shouldApplyRatingSort =
        Number.isInteger(selectedMin) && (!sort || sort === config.search.sortConfig.relevanceKey);
      const shouldApplyCountrySort = Boolean(exactCountry);

      const valueOf = l => {
        const pd = l?.attributes?.publicData || {};
        const v = typeof pd.tutorMinRating === 'number' ? pd.tutorMinRating : null;
        if (v != null) return v;
        const avg = typeof pd.ratingAvg === 'number' ? pd.ratingAvg : 0;
        return Math.round(avg * 10);
      };

      let dataToProcess = response?.data?.data || [];

      // Filter for Arabic from Scratch / Teaches from Scratch (Item 2.9)
      const rawTeachFromZeroParam = params.pub_teachFromZero;
      const rawSpecializationsParam = params.pub_specializations;
      const wantsTeachFromZero =
        rawTeachFromZeroParam === 'true' ||
        rawTeachFromZeroParam === 'yes' ||
        String(rawTeachFromZeroParam).includes('true') ||
        String(rawTeachFromZeroParam).includes('yes');
      const wantsArabicFromScratch =
        rawSpecializationsParam &&
        String(rawSpecializationsParam).toLowerCase().includes('arabic_from_scratch');

      if (wantsTeachFromZero || wantsArabicFromScratch) {
        dataToProcess = dataToProcess.filter(l => {
          const pd = l?.attributes?.publicData || {};
          const authorId = l?.relationships?.author?.data?.id?.uuid;
          const author = authorMap.get(authorId);
          const authorPd = author?.attributes?.profile?.publicData || {};

          const lSpecs = Array.isArray(pd.specializations) ? pd.specializations : [];
          const aSpecs = Array.isArray(authorPd.specializations) ? authorPd.specializations : [];
          const allSpecs = [...lSpecs, ...aSpecs].map(s => String(s).toLowerCase());

          const hasScratchSpec = allSpecs.includes('arabic_from_scratch');
          const hasTeachFromZero =
            pd.teachFromZero === 'true' ||
            pd.teachFromZero === true ||
            pd.teachFromZero === 'yes' ||
            authorPd.teachFromZero === 'true' ||
            authorPd.teachFromZero === true ||
            authorPd.teachFromZero === 'yes';

          return hasScratchSpec || hasTeachFromZero;
        });
      }

      // Filter for Teaching Categories ("What they teach" - 7 categories)
      const rawTeachingCatsParam = params.pub_teaching_categories || params.pub_teachingCategories;
      if (rawTeachingCatsParam) {
        const targetCats = String(rawTeachingCatsParam)
          .replace(/^has_any:/, '')
          .split(',')
          .map(s => s.trim().toLowerCase().replace(/[^a-z0-9]/g, ''))
          .filter(Boolean);

        if (targetCats.length > 0) {
          dataToProcess = dataToProcess.filter(l => {
            const pd = l?.attributes?.publicData || {};
            const authorId = l?.relationships?.author?.data?.id?.uuid;
            const author = authorMap.get(authorId);
            const authorPd = author?.attributes?.profile?.publicData || {};

            const lCats = getTeachingCategoriesFromPublicData(pd) || [];
            const aCats = getTeachingCategoriesFromPublicData(authorPd) || [];
            const allCats = [...lCats, ...aCats].map(c => String(c).toLowerCase().replace(/[^a-z0-9]/g, ''));

            if (targetCats.some(t => t.includes('scratch') || t.includes('zero'))) {
              if (
                pd.teachFromZero === 'true' ||
                pd.teachFromZero === true ||
                authorPd.teachFromZero === 'true' ||
                authorPd.teachFromZero === true
              ) {
                return true;
              }
            }

            return targetCats.some(tc => allCats.some(c => c.includes(tc) || tc.includes(c)));
          });
        }
      }

      const rawTeachingLangsParam = params.pub_teachingLanguages;

      if (rawTeachingLangsParam) {
        const targetLangs = String(rawTeachingLangsParam)
          .replace(/^has_any:/, '')
          .split(',')
          .map(s => s.trim().toLowerCase())
          .filter(Boolean);

        if (targetLangs.length > 0) {
          dataToProcess = dataToProcess.filter(l => {
            const pd = l?.attributes?.publicData || {};
            const authorId = l?.relationships?.author?.data?.id?.uuid;
            const author = authorMap.get(authorId);
            const authorPd = author?.attributes?.profile?.publicData || {};

            const extractLangStrings = val => {
              if (Array.isArray(val)) {
                return val
                  .map(item =>
                    typeof item === 'string'
                      ? item.toLowerCase()
                      : String(item?.key || item?.value || item?.option || '').toLowerCase()
                  )
                  .filter(Boolean);
              }
              if (typeof val === 'string') {
                return [val.toLowerCase()];
              }
              if (val && typeof val === 'object') {
                const s = String(val.key || val.value || val.option || '').toLowerCase();
                return s ? [s] : [];
              }
              return [];
            };

            const listingLangs = extractLangStrings(pd.teachingLanguages);
            const authorLangs = extractLangStrings(authorPd.teachingLanguages);

            // Legacy singular teachingLanguage support (e.g. 'en', 'ar', 'fr', 'es', 'de', 'tr', 'ur')
            const legacyCodeMap = {
              en: 'english',
              ar: 'arabic',
              fr: 'french',
              es: 'spanish',
              de: 'german',
              tr: 'turkish',
              ur: 'urdu',
            };
            const legacyLangs = [pd.teachingLanguage, authorPd.teachingLanguage]
              .filter(Boolean)
              .map(code => {
                const c = String(code).toLowerCase();
                return legacyCodeMap[c] || c;
              });

            const combinedLangs = [...listingLangs, ...authorLangs, ...legacyLangs];
            if (combinedLangs.length === 0) {
              const spoken = authorPd.spokenLanguage || pd.spokenLanguage || [];
              const available = getAvailableTeachingLanguages(spoken);
              combinedLangs.push(...available.map(a => a.value));
              if (combinedLangs.length === 0) {
                combinedLangs.push('arabic');
              }
            }
            return targetLangs.some(tl => {
              const tlLower = tl.toLowerCase();
              return combinedLangs.includes(tlLower) || combinedLangs.includes(legacyCodeMap[tlLower] || tlLower);
            });
          });
        }
      }

      // Exact in-memory price filter matching the card-rendered price in user's active display currency
      if (price) {
        const pValues = price.split(',');
        if (pValues.length === 2) {
          const filterMin = Number(pValues[0]);
          const filterMax = Number(pValues[1]);
          if (!isNaN(filterMin) && !isNaN(filterMax)) {
            dataToProcess = dataToProcess.filter(l => {
              const rawPrice = l?.attributes?.price;
              if (!rawPrice) return false;
              const amountInUnits = convertMoneyToNumber(rawPrice);
              const sourceCurrency = rawPrice.currency || 'USD';

              const authorId = l?.relationships?.author?.data?.id?.uuid;
              const author = authorMap.get(authorId);
              let effectiveBasePrice = amountInUnits;
              let effectiveSourceCurrency = sourceCurrency;
              const tutorBand = getTutorPriceBand(l, author);
              if (tutorBand && effectiveSourceCurrency === 'AED') {
                if (amountInUnits >= tutorBand.min && amountInUnits <= tutorBand.max) {
                  effectiveSourceCurrency = 'USD';
                } else {
                  const rawInUSD = convertCurrency(amountInUnits, 'AED', 'USD', fxRates).amount;
                  if (rawInUSD < tutorBand.min) {
                    effectiveBasePrice = tutorBand.min;
                    effectiveSourceCurrency = 'USD';
                  } else if (rawInUSD > tutorBand.max) {
                    effectiveBasePrice = tutorBand.max;
                    effectiveSourceCurrency = 'USD';
                  }
                }
              }

              const conv = convertCurrency(effectiveBasePrice, effectiveSourceCurrency, selectedCurrency, fxRates);
              const cardPrice = Math.ceil(conv.amount);
              return cardPrice >= filterMin && cardPrice <= filterMax;
            });
          }
        }
      }

      const sortedListings = [...dataToProcess].sort((a, b) => {
        if (shouldApplyCountrySort) {
          const pa = getCountryPriority(a);
          const pb = getCountryPriority(b);
          if (pa !== pb) return pa - pb;
        }

        if (shouldApplyRatingSort) {
          const av = valueOf(a);
          const bv = valueOf(b);
          if (av !== bv) return av - bv; // lower rating (but >= threshold) first
          const aAvg = a?.attributes?.publicData?.ratingAvg ?? 0;
          const bAvg = b?.attributes?.publicData?.ratingAvg ?? 0;
          if (aAvg !== bAvg) return aAvg - bAvg;
        }

        const aCreated = new Date(a?.attributes?.createdAt || 0).getTime();
        const bCreated = new Date(b?.attributes?.createdAt || 0).getTime();
        return bCreated - aCreated;
      });

      // When post-query or in-memory filtering results in 0 matching listings (e.g. empty category),
      // override pagination metadata so phantom pagination controls from fallback queries are not rendered.
      const meta =
        dataToProcess.length === 0
          ? {
              ...response.data?.meta,
              totalItems: 0,
              totalPages: 0,
            }
          : response.data?.meta;

      const sortedResponse = {
        data: {
          ...response.data,
          meta,
          data: sortedListings,
        },
      };

      // Dialect group fallback matching (Defect B requirement):
      // If exact country has 0 tutors, automatically match sister countries in dialect cluster;
      // if none in cluster, match MSA tutors.
      // Search page banner notice:
      // “No {Country/Dialect} tutors are available at the moment. The tutors below teach a closely related Arabic.”
      let fallbackNotice = null;
      if (exactCountry && exactCountry !== 'MSA' && exactCountry !== 'NOT_SURE') {
        const hasExactMatch = dataToProcess.some(l => getCountryPriority(l) === 1);
        if (!hasExactMatch && dataToProcess.length > 0) {
          const hasClusterOrRelated = dataToProcess.some(l => {
            const p = getCountryPriority(l);
            return p > 1;
          });
          if (hasClusterOrRelated) {
            fallbackNotice = {
              dialect: countryDialect?.dialectName || countryDialect?.countryName || selectedTargetCountry,
            };
          }
        }
      }

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(sortedResponse, fallbackNotice));
      try {
        const listings = response?.data?.data || [];
        const ids = listings.map(l => l.id).filter(Boolean);
        // if (ids.length) dispatch(fetchReviewSummaries(ids));
      } catch (err) {
        // ignore
      }
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      pub_listingType: 'book-seats',
      include: ['author', 'images', 'author.profileImage'],
      // 'fields.listing': [
      //   'title',
      //   'geolocation',
      //   'price',
      //   'deleted',
      //   'state',
      //   'publicData.listingType',
      //   'publicData.transactionProcessAlias',
      //   'publicData.unitType',
      //   'publicData.cardStyle',
      //   // These help rendering of 'purchase' listings,
      //   // when transitioning from search page to listing page
      //   'publicData.pickupEnabled',
      //   'publicData.shippingEnabled',
      //   'publicData.priceVariationsEnabled',
      //   'publicData.priceVariants',
      // ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );
  return dispatch(searchListingsCall);
};
